import moment from 'moment'

import i18n from 'modules/core/lib/i18n'
import { Model } from 'modules/core/lib/mobx-rest'

export interface IShiftVersionHistory {
  type: string | null
  created_at: number
  author_id: number | null
  author_name: string | null
  location_type: string | null
  browser_platform_name: string
  shift_id: number
  workable: boolean
  automatic_clock_in: boolean
  clock_in: string | null
  clock_out: string | null
  system_created: boolean | null
  has_observations_changed: boolean
  has_location_type_changed: boolean
  has_clock_in_out_changed: boolean
  forgot_clock_out: boolean | null
  latitude?: number
  longitude?: number
  new_clock_in?: string | null
  new_clock_out?: string | null
  old_clock_in?: string | null
  old_clock_out?: string | null
  old_observations?: string | null
  new_observations?: string | null
  old_location_type?: string | null
  new_location_type?: string | null
}

export const SHIFT_CHANGE_TYPES = {
  creation: 'creation',
  observation: 'observation',
  location_type: 'location_type',
  clock_in_out: 'clock_in_out',
  breaks: 'breaks',
}

class ShiftVersionHistory extends Model<IShiftVersionHistory> {
  static urlRoot() {
    return '/attendance/shift_version_histories'
  }

  static SHIFT_VERSION_HISTORY_TYPES = {
    created: 'created',
    updated: 'updated',
    clockedIn: 'clocked_in',
    clockedOut: 'clocked_out',
    observationUpdated: 'observation_updated',
    locationTypeUpdated: 'location_type_updated',
  }

  get authorName(): string {
    const authorName = this.attributes.author_name
    if (authorName) return authorName
    else return i18n.t('clock_in.history.system_author')
  }

  get type(): string {
    if (this.hasObservationsChanged) {
      return ShiftVersionHistory.SHIFT_VERSION_HISTORY_TYPES.observationUpdated
    }
    if (this.hasLocationTypeChanged) {
      return ShiftVersionHistory.SHIFT_VERSION_HISTORY_TYPES.locationTypeUpdated
    }

    return this.attributes.type ?? ''
  }

  get browserPlatformName(): string {
    return this.attributes.browser_platform_name
  }

  get locationType(): string | null {
    return this.attributes.location_type
  }

  get hasLatitudeAndLongitude(): boolean {
    return !!this.latitude && !!this.longitude
  }

  get hasBrowserPlatformName(): boolean {
    return this.attributes.browser_platform_name !== ''
  }

  get isForgotClockOut(): boolean {
    return !!this.attributes.forgot_clock_out
  }

  get createdAt(): moment.Moment {
    return moment.unix(+this.attributes.created_at)
  }

  get latitude(): number | null {
    if (this.has('latitude')) {
      return this.attributes.latitude ?? null
    }

    return null
  }

  get longitude(): number | null {
    if (this.has('longitude')) {
      return this.attributes.longitude ?? null
    }

    return null
  }

  get clockIn(): string | null {
    if (this.has('clock_in')) {
      return this.attributes.clock_in
    }

    return null
  }

  get clockOut(): string | null {
    if (this.has('clock_out')) {
      return this.attributes.clock_out
    }

    return null
  }

  get typeClockedIn(): boolean {
    return this.type === ShiftVersionHistory.SHIFT_VERSION_HISTORY_TYPES.clockedIn
  }

  get typeClockedOut(): boolean {
    return this.type === ShiftVersionHistory.SHIFT_VERSION_HISTORY_TYPES.clockedOut
  }

  get typeUpdated(): boolean {
    return this.type === ShiftVersionHistory.SHIFT_VERSION_HISTORY_TYPES.updated
  }

  get typeCreated(): boolean {
    return this.type === ShiftVersionHistory.SHIFT_VERSION_HISTORY_TYPES.created
  }

  get typeCreatedOrUpdated(): boolean {
    return (
      this.typeCreated ||
      this.typeUpdated ||
      this.hasObservationsChanged ||
      this.hasLocationTypeChanged
    )
  }

  get typeClockedInOrClockedOut(): boolean {
    return this.typeClockedIn || this.typeClockedOut
  }

  get hasLocationType(): boolean {
    return !!this.locationType
  }

  get hasObservationsChanged(): boolean {
    return this.attributes.has_observations_changed
  }

  get hasLocationTypeChanged(): boolean {
    return this.attributes.has_location_type_changed
  }

  get hasClockInOutChanged(): boolean {
    return this.attributes.has_clock_in_out_changed
  }

  get shiftTypeBreak(): boolean {
    return !!this.attributes.workable
  }

  get typeAutomaticBreak(): boolean {
    return this.shiftTypeBreak && !!this.attributes.system_created
  }

  get clockedInWithLocationType(): boolean {
    return this.typeClockedIn && this.hasLocationType
  }

  get clockedInWithLocationTypeAndLat(): boolean {
    return this.clockedInWithLocationType && this.hasLatitudeAndLongitude
  }

  get changes(): string[] {
    const array = []

    if (this.typeCreated) array.push(SHIFT_CHANGE_TYPES.creation)

    if (this.typeAutomaticBreak) array.push(SHIFT_CHANGE_TYPES.breaks)

    if (this.hasObservationsChanged) array.push(SHIFT_CHANGE_TYPES.observation)
    if (this.hasLocationTypeChanged) array.push(SHIFT_CHANGE_TYPES.location_type)

    if (this.hasClockInOutChanged) array.push(SHIFT_CHANGE_TYPES.clock_in_out)

    return array
  }
}

export default ShiftVersionHistory
